import * as React from "react";
import { Spinner } from "@finster/ui";
import { Monitor } from "lucide-react";
import { useTheme } from "../ThemeContext";

/**
 * Sign-in surface for the Office add-in.
 *
 * Two operating modes:
 *
 *  - **AuthKit build** (`enableStandaloneSsoLogin = false`, default):
 *    one big "Login" button. `onLogin` opens the WorkOS AuthKit
 *    dialog with no preferred connection.
 *
 *  - **FactSet standalone-SSO build** (`enableStandaloneSsoLogin = true`):
 *    one big "Continue with FactSet" button. `onLogin` opens the
 *    Office dialog with `method: "factset"` so it short-circuits
 *    straight to the FactSet IdP.
 *
 * There is intentionally no email/password path on the SSO build.
 * FactSet end users are already authenticated by FactSet's IdP, so
 * the SSO round-trip is normally invisible — adding an email form
 * was a dead detour that just routed the same user through the
 * same dialog with extra clicks. Internal admins on the SSO build
 * sign in via FactSet too; if a true password path is ever needed
 * again, reintroduce it as a separate component or stage rather
 * than re-adding it here.
 */
interface LoginProps {
  onLogin: () => Promise<void>;
  enableStandaloneSsoLogin?: boolean;
  headlineTemplate?: string;
  subheadline?: string;
}

const DEFAULT_HEADLINE_TEMPLATE = "{brand} AI, right in your slides";
const DEFAULT_SUBHEADLINE =
  "Build slides, make edits, and iterate on your presentation in real time.";

export const Login: React.FC<LoginProps> = ({
  onLogin,
  enableStandaloneSsoLogin = false,
  headlineTemplate = DEFAULT_HEADLINE_TEMPLATE,
  subheadline = DEFAULT_SUBHEADLINE,
}) => {
  const { brandName } = useTheme();
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const headline = headlineTemplate.replace("{brand}", brandName);
  const description = subheadline;
  const buttonLabel = enableStandaloneSsoLogin ? "Continue with FactSet" : "Login";

  const handleLogin = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await onLogin();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-white px-6">
      <div className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-[#e8f4fd]">
        <Monitor className="h-8 w-8 text-[#0076d6]" strokeWidth={1.5} />
      </div>

      <h1 className="mt-5 text-center text-[22px] font-semibold leading-tight tracking-[-0.01em] text-[#131722]">
        {headline}
      </h1>
      <p className="mt-2 max-w-[280px] text-center text-[14px] leading-[1.5] text-[#707783]">
        {description}
      </p>

      <button
        type="button"
        onClick={handleLogin}
        disabled={isLoading}
        className="mt-8 flex h-[44px] w-full max-w-[300px] items-center justify-center rounded-lg bg-[#0076d6] text-[15px] font-semibold text-white transition hover:bg-[#0668bb] disabled:opacity-60"
      >
        {isLoading && <Spinner size="sm" />}
        <span className={isLoading ? "ml-2" : ""}>{buttonLabel}</span>
      </button>

      {error && (
        <p className="mt-4 max-w-[300px] rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};

export default Login;
